import qs from "query-string";
import { AxiosResponse } from "axios";

import ApiService from "./apiService";

import { BEConfig } from "../config/env";

import { webapiGet, webapiPost } from "../webapis/core";
import { postDashboardWebApi } from "@/webapis/apiResource";

class LoyaltyPointsService extends ApiService {
  getBaseUrl() {
    const { baseURL, protocol, port, versionInfo } = BEConfig.customerApi;

    const url = `${protocol}${baseURL}${port}${versionInfo}`;

    return url;
  }

  isArrayDataValid = array =>
    array.every(
      el => (el.data && el.data.success) || el.data.success === false
    );

  async getLoyaltyExpiryDetails(countryId, currencyCode, language) {
    const apiURL = `${this.getBaseUrl()}${
      BEConfig.customerApi.loyaltyExpiryDetails
    }`;

    const queryParams = qs.stringify({
      countryId,
      currencyCode
    });

    const endpoint = `${apiURL}?${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId(),
      language
    ).request;

    if (!response.data.success) throw new Error(response.data.message);

    return response.data;
  }

  async getLoyaltyEarnedPoints(countryId, currencyCode, language) {
    const apiURL = `${this.getBaseUrl()}${
      BEConfig.customerApi.loyaltyEarnedPoints
    }`;

    const queryParams = qs.stringify({
      countryId,
      currencyCode
    });

    const endpoint = `${apiURL}?${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId(),
      language
    ).request;

    if (!response.data.success) throw new Error(response.data.message);

    return response.data;
  }

  async getLoyaltyDetails(language: Language) {
    const endpoint = `${this.getBaseUrl()}${
      BEConfig.customerApi.getLoyaltyDetails
    }`;

    const response: AxiosResponse<GetLoyaltyDetailsResponse> = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId(),
      language
    ).request;

    return response.data;
  }
  async getLoyaltyCardDetails(language: Language) {
    const endpoint = `${this.getBaseUrl()}${
      BEConfig.customerApi.loyaltyCardDetails
    }`;

    const response: AxiosResponse<LoyaltyCardDetailsData> = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId(),
      language
    ).request;

    return response.data;
  }

  async getWalletHistory(currencyCode, countryId, language, pageNum) {
    const queryParams = qs.stringify({
      currencyCode,
      countryId
    });
    const walletHistoryEndpoint = `${this.getBaseUrl()}${
      BEConfig.customerApi.wallet
    }?pageSize=4&pageNum=${pageNum}&${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      walletHistoryEndpoint,
      this.getVisitorId(),
      language
    ).request;

    if (!response.data.success) throw new Error(response.data.message);

    return response;
  }

  async getWalletBalance(currencyCode, countryId, language) {
    const queryParams = qs.stringify({
      currencyCode,
      countryId
    });
    const walletBalanceEndpoint = `${this.getBaseUrl()}${
      BEConfig.customerApi.walletBalance
    }?${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      walletBalanceEndpoint,
      this.getVisitorId(),
      language
    ).request;

    if (!response.data.success) throw new Error(response.data.message);

    return response;
  }

  async getMyWalletDetails(currencyCode, countryId, language, pageNum) {
    const response = await Promise.all([
      this.getWalletHistory(currencyCode, countryId, language, pageNum),
      this.getWalletBalance(currencyCode, countryId, language)
    ]);

    if (!this.isArrayDataValid(response)) {
      // TODO: fix ts error
      // @ts-ignore
      throw new Error(response);
    }

    return response;
  }

  async getWalletExpiryDetails(countryId, currencyCode, language) {
    const apiURL = `${this.getBaseUrl()}${
      BEConfig.customerApi.walletExpirationDetails
    }`;

    const queryParams = qs.stringify({
      countryId,
      currencyCode
    });

    const endpoint = `${apiURL}?${queryParams}`;

    const response = await webapiGet(
      this.getAccessToken(),
      endpoint,
      this.getVisitorId(),
      language
    ).request;

    if (!response.data.success) throw new Error(response.data.message);

    return response.data;
  }

  async downloadInvoice(bodyData) {
    const apiURL = `${this.getBaseUrl()}${
      BEConfig.customerApi.downloadLoyaltyInvoice
    }`;

    try {
      const { data: responseData } = await webapiPost(
        this.getAccessToken(),
        apiURL,
        bodyData
      ).request;

      if (responseData?.data && responseData?.code === 200) {
        const file = responseData.data.invStr;
        const linkSource = `data:application/pdf;base64,${file}`;
        const downloadLink = document.createElement("a");
        const fileName = `${bodyData.invoiceNo}.pdf`;
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();

        return { ...responseData, status: true };
      } else {
        return { ...responseData, status: false };
      }
    } catch (error) {
      console.error(error);
    }
  }

  private async getReciprociIframeInfo(
    pageType: ReciprociPageType
  ): Promise<ReciprociIframeInfo> {
    const { baseURL, protocol, port, versionInfo, getLoyaltyProgramIframeURL } =
      BEConfig.customerApi;

    const url = `${protocol}${baseURL}${port}${versionInfo}${getLoyaltyProgramIframeURL}`;

    try {
      const { data: responseData } = await webapiPost(
        this.getAccessToken(),
        url,
        {
          pageType
        }
      ).request;
      return responseData;
    } catch (error) {
      console.error(error);
      throw new Error("Error fetching iframe info");
    }
  }

  public getLoyaltyProgramIframeInfo() {
    return this.getReciprociIframeInfo(ReciprociPageType.LOYALTY);
  }

  public getMyWalletIframeInfo() {
    return this.getReciprociIframeInfo(ReciprociPageType.WALLET);
  }
  async getLoyaltyPointsByCart(
    products: LoyaltyPointsPayload[],
    page: "cart" | "pdp"
  ): Promise<number> {
    const response = (await postDashboardWebApi(
      this.getAccessToken(),
      `${this.getBaseUrl()}${BEConfig.customerApi.getLoyaltyPointsByProducts}`,
      { products, page },
      this.getVisitorId()
    )) as AxiosResponse<{
      success: boolean;
      message: string;
      totalPoints: number;
    }>;

    return response.data?.totalPoints;
  }
}

export type GetLoyaltyDetailsResponse = {
  success: boolean;
  message: string;
  data: LoyaltyDetailsData;
};

export type LoyaltyCardDetailsData = {
  customerIdBarCode: string;
  customerBarcodeExpiresIn: string;
  points: number;
  pointsWorth: number;
  currency: string;
  customerTierName: string;
  customerTierEnglishName: "Explorer" | "Hunter" | "Champion";
  customerNextTierName: string;
  ewalletBalance: string;
  ewalletBalanceExpiryAmount: string;
  ewalletBalanceExpiryDate: string;
  pointsToTierUpgrade: {
    pointsAmount: number;
    pointsExpiringDate: string;
  };
  pointsExpirySoonDetails: {
    points: number;
    pointsExpiringDate: string;
  };
};

export type LoyaltyDetailsData = {
  // null - if we are not registered in the loyalty program
  loyaltyDetails: LoyaltyDetails | null;
  tierToken: string;
};

export type LoyaltyDetails = {
  cardNumber: string;
  memeberLevel: string;
  currentBalance: string;
  pointsValue: string;
  pointNeededForNextLevel: number;
  usedPointsToConvert: number;
  earnedAmountFromConvert: number;
  accuralPoints: number;
  currency: string;
  cutoffDate: Date;
};

export type ReciprociIframeInfo = {
  url: string;
  expiresIn: number;
  pageType: ReciprociPageType;
};

export enum ReciprociPageType {
  LOYALTY = "loyalty",
  WALLET = "ewallet"
}

type LoyaltyCategoryType = {
  key: string;
  label: string;
  defaultValue: string;
  id: number;
};

export type LoyaltyPointsPayload = {
  productId: number;
  quantity: number;
  transactionPrice: number;
  brand: LoyaltyCategoryType;
  parentCategory: LoyaltyCategoryType;
  category: LoyaltyCategoryType;
  subcategory: LoyaltyCategoryType;
  price: {
    current: number;
    currentPriceLabel: string;
    old: number;
    oldPriceLabel: string;
    discountPercentage: number;
    currency: string;
    rrp: {
      price: number;
      priceLabel: string;
      savedPrice: number;
      savedPriceLabel: string;
      discountPercentage: number;
    };
  };
};

const instance = new LoyaltyPointsService();

export default instance;
